import { CellClickedEvent, ColDef, GridApi, GridOptions, GridReadyEvent, Module, RowClassParams, ValueFormatterParams } from '@ag-grid-community/core';
import { Component, OnInit, Inject, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { BaseModalComponent } from '../base-modal.component';
import { ModalsService } from '../modals.service';
import { ApiService } from '../../services/api-service/api-service';
import { CategoryTagsComponent } from '../../components/server-side-grid/category-tags-renderer/category-tags-renderer.component';
import { TasksNotesPanelCollections } from '../../components/tasks-notes-activity-table/tasks-notes-activity-table.component';
import { EM_DASH } from '../../constants/em-dash.constant';
import moment from 'moment';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICaringAdultInfo } from '../ecfik-program-point/program-point-modal/program-point-modal.component';
import { SpinnerModalComponent } from '../../components/spinner/spinner-modal.component';
import { AgTooltipComponent } from '../../components/ag-grid/ag-tooltip/ag-tooltip.component';
import { MenuCellRenderer } from '../../components/server-side-grid/menu-cell-renderer/menu-cell-renderer.component';
import { NoRowsComponent } from '../../components/server-side-grid/no-rows/no-rows.component';
import { HistoryAllModalMoreButtonComponent } from '../../components/server-side-grid/history-all-modal-more-button/history-all-modal-more-button.component';
import { PillCellRenderer } from '../../components/server-side-grid/pill-cell-renderer/pill-cell-renderer.component';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { PartnerTypes } from '../../typings/interfaces/partner.interface';
import { ECFIKManagementService } from '../../../school/ecfik-management/ecfik-management.service';
import { BehaviorSubject, filter, finalize, from, map, mergeMap, Observable, of, switchMap, take, tap, toArray } from 'rxjs';
import { IUser } from '../../typings/interfaces/user.interface';
import { IConfirmModalComponentData } from '../confirm/confirm-modal.component';
import { SpinnerService } from '../../components/spinner/spinner-modal.service';
import { SnackBarService } from '../../services/snackbar/snackbar.service';
import { INotesData } from '../notes/notes-modal-shell.component';
import { TSchoolHistoryLogsComponent } from '../history-all/history-all-modal.component';
import { ISchool } from '../../typings/interfaces/school.interface';
import { getSchool } from 'Src/ng2/store';
import { select, Store } from '@ngrx/store';
import { ICheckInLogData } from '../check-in-logs/check-in-log-modal.component';
import { ISchoolNote } from '../../typings/interfaces/note.interface';
import { IFamily } from 'projects/shared/typings/interfaces/family.interface';

export type TEcfikNotesModalControl = 'CARING_ADULT' | 'STUDENT';

export interface IEcfikNotesRes {
  data: ISchoolNote[];
  items: {
    begin: number;
    end: number;
    limit: number;
    total: number;
  };
  pages: {
    current: number;
    hasNext: boolean;
    hasPrev: boolean;
    next: number;
    prev: number;
    total: number;
  };
}

export interface IEcfikNotesModalData {
  modalControl: TEcfikNotesModalControl;
  schoolId: string;
  studentIds: string[];
  studentInfo?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  caringAdultInfo?: ICaringAdultInfo;
  currentUser?: IUser;
  }

@Component({
  selector: 'ecfik-notes-modal',
  templateUrl: './ecfik-notes-modal.component.html',
  styleUrls: ['./ecfik-notes-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EcfikNotesModal extends BaseModalComponent implements OnInit {
  @Output() saveStudent: EventEmitter<any> = new EventEmitter();

  public modalControl: TEcfikNotesModalControl;
  private schoolId: string;
  public pillText: string;
  public pillColor = 'blue-light-6 dark-text font-weight-normal';

  private spinner: MatDialogRef<SpinnerModalComponent>;

  public showGrid: boolean = false;
  public noRowOverlayConfig: any;
  public listType: TSchoolHistoryLogsComponent = 'STUDENT';
  public partnerType: PartnerTypes = PartnerTypes.SCHOOL;
  public currentUser: IUser;
  public currentSchool: ISchool;
  public studentIds: string[];

  private familyChildrenNamesMap$ = new BehaviorSubject<Record<string, string[]>>({});

  // Ag-grid
  private gridApi: GridApi;
  public modules: Module[] = [ClientSideRowModelModule];
  public gridOptions: GridOptions;
  public columnDefs: ColDef[];
  private currentSelectedRow: any;
  public rowData!: any[];

  // Paginator
  public currentPage: number = 1;
  public totalPages: number;
  public totalRows: number;
  public pageSize: number = 10;

  private shouldRefreshParent: boolean = false;

  constructor (
    dialogRef: MatDialogRef<EcfikNotesModal>,
    @Inject(MAT_DIALOG_DATA) public data: IEcfikNotesModalData,
    private apiService: ApiService,
    private modalsService: ModalsService,
    private ecfikManagementService: ECFIKManagementService,
    private spinnerService: SpinnerService,
    private snackBarService: SnackBarService,
    private store: Store,
  ) {
    super(dialogRef);
    // Prevent outside click from auto-closing without custom logic
    this.dialogRef.backdropClick().subscribe(() => {
      this.dialogRef.close(this.shouldRefreshParent); // or null
    });

    // prevent ESC key default close
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close(this.shouldRefreshParent);
      }
    });
  }

  ngOnInit () {
    this.modalControl = this.data.modalControl;
    this.schoolId = this.data.schoolId;
    this.currentUser = this.data.currentUser;
    this.studentIds = this.data.studentIds;

    const name = this.modalControl === 'CARING_ADULT' ? this.data.caringAdultInfo : this.data.studentInfo;
    this.pillText = this.modalControl === 'CARING_ADULT' && 'Caring Adult';
    this.title = this.getTitle(name);

    this.store
      .pipe(
        select(getSchool),
        take(1),
        tap((school: ISchool) => {
          this.currentSchool = school;
        }),
      )
      .subscribe();

    this.mapFamilyDataByStudentIds$(this.studentIds)
      .pipe(
        take(1),
        tap((familyData) => {
          this.familyChildrenNamesMap$.next(familyData);
        }),
      )
      .subscribe();

    this.refreshDataForStudent(this.currentPage);
    // If we show grid immediately after clicking the notes icon in `ecfik-manage-program-caring-adults`, we will get a Maximum call stack size exceeded due to TabGuard.findNextElementOutsideAndFocus being caught in a loop
    this.showGrid = true;

    this.columnDefs = this._getColumnDef();
    this.noRowOverlayConfig = this.getNoRowOverlayConfig();
    this.gridOptions = {
      rowModelType: 'clientSide',
      getRowId: ({ data: { _id } }) => (_id),
      domLayout: 'autoHeight', // sets the custom paginator to the bottom of the grid
      rowSelection: {
        mode: 'singleRow',
        checkboxes: false,
        enableClickSelection: true,
      },
      rowHeight: 50,
      pagination: true,
      paginationPageSize: this.pageSize,
      suppressPaginationPanel: true,
      suppressScrollOnNewData: true,
      suppressContextMenu: true,
      suppressCellFocus: true,
      components: {
        noRowsComponent: NoRowsComponent,
        historyAllModalMoreButtonCellRenderer: HistoryAllModalMoreButtonComponent,
        CategoryTagsComponent,
        MenuCellRenderer,
        PillCellRenderer,
        agCustomTooltip: AgTooltipComponent,
      },
      defaultColDef: {
        resizable: false,
        sortable: false,
      },
      noRowsOverlayComponent: 'noRowsComponent',
      noRowsOverlayComponentParams: this.noRowOverlayConfig,
      onFirstDataRendered: (params) => {
        params.api.sizeColumnsToFit();
      },
      rowClassRules: {
        'row-grey': this._getRowColor,
      },
    };
  }

  private getTitle (data: { firstName: string; lastName: string }): string {
    if (data) {
      const name = `${data.firstName} ${data.lastName}`;
      return `ECFIK Notes for ${name}`;
    }
    return '';
  }

  private _getRowColor (params: RowClassParams): boolean {
    const {
      data: { status },
    } = params;
    if (!status) return false;
    else return status !== 'ACTIVE';
  }

  public onGridReady (params: GridReadyEvent<any>) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
  }

  public refreshDataForStudent (page: number): void {
    const payload = this._buildPayload(page);

    this.ecfikManagementService.getPaginatedEcfikNotesData(payload).pipe(
      take(1),
      tap({
        next: (data) => this._processPaginatedData(data),
        error: (err) => this._handleError(err),
      }),
      finalize(() => this.spinner?.close()), // regardless of success or error
    ).subscribe();
  }

  private getFamilyData$ (schoolId: string, studentId: string): Observable<IFamily> {
    return this.ecfikManagementService.getFamilyNames$(schoolId, studentId);
  }

  // TODO: Create new endpoint to get family data for multiple students in one request then refactor this
  private mapFamilyDataByStudentIds$ (studentIds: string[]): Observable<Record<string, string[]>> {
    if (!studentIds?.length) return of(null);
    const seenFamilyIds = new Set<string>();

    return from(studentIds).pipe(
      mergeMap(studentId =>
        this.getFamilyData$(this.schoolId, studentId).pipe(
          filter(family => {
            if (family) {
              if (seenFamilyIds.has(family.familyId)) {
                return false;
              }
              seenFamilyIds.add(family.familyId);
              return true;
            } else {
              return false;
            }
          }),
        ),
      ),
      toArray(),
      map(familyArray => {
        const familyMap: Record<string, string[]> = {};
        for (const family of familyArray) {
          familyMap[family.familyId] = family.children.map(child => `${child.firstName} ${child.lastName}`);
        }
        return familyMap;
      }),
    );
  }

  private _buildPayload (page: number): any {
    const collection = TasksNotesPanelCollections.NOTES;
    const payload = {
      ...(this.modalControl === 'STUDENT' && { studentId: null }),
      schoolId: this.schoolId,
      partnerType: PartnerTypes.SCHOOL,
      collection,
      calcValueField: null,
      userSelectedFilters: null,
      page,
      limit: this.pageSize,
    };
    if (this.modalControl === 'CARING_ADULT' && this.data?.caringAdultInfo?.id) {
      payload.userSelectedFilters = this._serializeFilters({ createdByUserId: this.data.caringAdultInfo?.id });
    }
    if (this.modalControl === 'STUDENT' && this.data?.studentInfo?.id) {
      payload.studentId = this.data.studentInfo.id;
      payload.userSelectedFilters = this._serializeFilters({ categories: ['ECFIK'] });
    }
    return payload;
  }

  private _serializeFilters (filters: any): string | null {
    if (!filters) return null;
    return JSON.stringify(filters).replace(/"/g, '\\"');
  }

  private _handleError (error: any): void {
    console.error('Error fetching Notes data:', error);
  }

  public onCellClicked (event: CellClickedEvent) {
    const colDef = event.colDef;
    const selectedRows = event.api.getSelectedRows();
    const rowData = selectedRows[0];
    this.currentSelectedRow = rowData;
    // Do not select rows if the 'Three dot menu' column was clicked
    const headerName = colDef.headerName;
    if (headerName === '') return;
    const isSelected = event.node.isSelected();
    if (isSelected) {
      const { attachments, categories, body, createdBy, dateCreated, ecfik } = this.currentSelectedRow;
      const childrenNames = this.familyChildrenNamesMap$.value[ecfik?.familyId];
      const nameFormatted = childrenNames?.length ? childrenNames.join(', ') : this.formatStudentName(attachments.student.lastFirst);
      const dateFormatted = moment(dateCreated).format('MMM DD, YYYY');
      const logDateFormatted = ecfik?.logDate && moment(ecfik.logDate).format('MMM DD');
      const data = [
        { sectionTitle: childrenNames?.length ? 'Student names' : 'Student name', sectionText: nameFormatted },
        { sectionTitle: 'Categories', sectionText: categories.join(', ') },
        { sectionTitle: 'Note', sectionText: body },
        ...(ecfik?.logDate ? [{ sectionTitle: 'Date', sectionText: logDateFormatted }] : []),
        ...(ecfik?.type ? [{ sectionTitle: 'Type', sectionText: ecfik.type }] : []),
        ...(ecfik?.status ? [{ sectionTitle: 'Status', sectionText: ecfik.status }] : []),
        ...(ecfik?.needs ? categories.includes('ECFIK') && [{ sectionTitle: 'Needs (optional)', sectionText: ecfik.needs.length ? ecfik.needs.join(', ') : '-' }] : []),
        ...(ecfik?.serviceReferral ? categories.includes('ECFIK') && [{ sectionTitle: 'Service referral', sectionText: ecfik.serviceReferral === true ? 'Yes' : 'No' }] : []),
        { sectionTitle: 'Added by', sectionText: `${createdBy.firstName} ${createdBy.lastName} on ${dateFormatted}` },
      ];
      this.openNoteDetailsModal(data);
    }
  }

  private formatStudentName (studentName: string): string {
    if (!studentName) return '';
    return studentName.toLocaleLowerCase().replace(/\b\w/g, firstLetter => firstLetter.toUpperCase());
  }

  public openNoteDetailsModal (rowData: any) {
    this.modalsService
      .openNoteDetailsModal({ rowData, listType: this.listType })
      .afterClosed()
      .subscribe(res => {
        this.gridApi.deselectAll();
      });
  }

  private _getColumnDef (): ColDef[] {
    const FIRST_COLUMN_WIDTH = 300;

    const COLUMN_DEFS_NOTES: ColDef[] = this.modalControl === 'CARING_ADULT'
      ? [
          {
            field: 'notes',
            headerName: 'Notes',
            width: FIRST_COLUMN_WIDTH,
            wrapText: true,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-first-column-custom-text',
            cellClass: 'cell-custom-text',
            cellStyle: {
              wordBreak: 'pre-line',
            },
            cellRenderer: CategoryTagsComponent,
            cellRendererParams: {
              listType: this.listType,
              collection: TasksNotesPanelCollections.NOTES,
              displayCatsInBody: false,
            },
          },
          {
            field: 'student',
            headerName: 'Student',
            flex: 1,
            wrapText: true,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-custom-text',
            cellClass: ['cell-custom-text'],
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params?.node?.data) return EM_DASH;
              const { student: { lastFirst } } = params.data.attachments;
              const familyId = params.node.data.ecfik?.familyId;

              const splitLastFirst = lastFirst?.split(', ');
              if (!splitLastFirst) return EM_DASH;

              const [lastName, firstName] = splitLastFirst;
              const formattedStudentName = this.formatStudentName(`${firstName} ${lastName}`);
              if (familyId) {
                const familyChildrenNames = this.familyChildrenNamesMap$.value[familyId];
                if (familyChildrenNames?.length > 1) {
                  return `${formattedStudentName}, +${familyChildrenNames.length - 1}`;
                } else {
                  return formattedStudentName;
                }
              }
              return formattedStudentName;
            },
          },
          {
            field: 'notes',
            headerName: 'Check-in type',
            flex: 1,
            wrapText: true,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-custom-text',
            cellClass: ['cell-custom-text'],
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params.node?.data?.ecfik) return EM_DASH;
              const { ecfik } = params.node.data;
              return ecfik.type || EM_DASH;
            },
          },
          {
            field: 'notes',
            headerName: 'Needs',
            flex: 1,
            wrapText: true,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-custom-text',
            cellClass: ['cell-custom-text'],
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params.node?.data?.ecfik) return EM_DASH;
              const { ecfik } = params.node.data;
              const needs = ecfik?.needs?.join(', ') || EM_DASH;
              return needs;
            },
          },
          {
            field: 'addedOn',
            headerName: 'Added on',
            flex: 1,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-custom-text',
            cellClass: ['cell-custom-text'],
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params?.node?.data) return EM_DASH;
              const createdAt = params.data.dateCreated;
              const dateFormatted = moment.utc(createdAt).format('MMM D, YYYY');
              return dateFormatted;
            },
          },
          {
            field: 'historyAllModalMoreButton',
            headerName: '',
            width: 48,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            cellClass: ['cell-custom-text', 'cell-custom-text-center'],
            cellRenderer: 'historyAllModalMoreButtonCellRenderer',
            cellRendererParams: {
              collection: 'notes',
              parentComponent: this,
            },
          },
        ]
      : [
          {
            field: 'notes',
            headerName: 'Notes',
            width: FIRST_COLUMN_WIDTH,
            wrapText: true,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-first-column-custom-text',
            cellClass: 'cell-custom-text',
            cellStyle: {
              wordBreak: 'pre-line',
            },
            cellRenderer: CategoryTagsComponent,
            cellRendererParams: {
              listType: this.listType,
              collection: TasksNotesPanelCollections.NOTES,
              displayCatsInBody: false,
            },
          },
          {
            field: 'notes',
            headerName: 'Check-in type',
            flex: 1,
            wrapText: true,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-custom-text',
            cellClass: ['cell-custom-text'],
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params.node?.data?.ecfik) return EM_DASH;
              const { ecfik } = params.node.data;
              return ecfik.type || EM_DASH;
            },
          },
          {
            field: 'notes',
            headerName: 'Needs',
            flex: 1,
            wrapText: true,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-custom-text',
            cellClass: ['cell-custom-text'],
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params.node?.data?.ecfik) return EM_DASH;
              const { ecfik } = params.node.data;
              const needsCount = ecfik?.needs?.length;
              if (needsCount) {
                const needs = ecfik?.needs?.join(', ') || EM_DASH;
                return needsCount > 1 ? `${needs[0]}, +${needsCount - 1}` : ecfik.needs[0];
              }
              return EM_DASH;
            },
          },
          {
            field: 'addedBy',
            headerName: 'Added by',
            flex: 1,
            wrapText: true,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-custom-text',
            cellClass: ['cell-custom-text'],
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params.node.data) return;
              // Replace 'ETL User' with 'The Portal' to indicate logs created by an automated process.
              const { firstName, lastName } = params.data.createdBy;

              if (firstName?.toLowerCase() === 'etl' && lastName?.toLowerCase() === 'user') {
                return 'The Portal';
              }

              return `${firstName} ${lastName}`;
            },
          },
          {
            field: 'addedOn',
            headerName: 'Added on',
            flex: 1,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            headerClass: 'header-custom-text',
            cellClass: ['cell-custom-text'],
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params?.node?.data) return EM_DASH;
              const createdAt = params.data.dateCreated;
              const dateFormatted = moment.utc(createdAt).format('MMM D, YYYY');
              return dateFormatted;
            },
          },
          {
            field: 'historyAllModalMoreButton',
            headerName: '',
            width: 48,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            cellClass: ['cell-custom-text', 'cell-custom-text-center'],
            cellRenderer: 'historyAllModalMoreButtonCellRenderer',
            cellRendererParams: {
              collection: 'notes',
              parentComponent: this,
            },
          },
    ];

    return COLUMN_DEFS_NOTES;
  }

  private getNoRowOverlayConfig (): any {
    const noRowOverlayConfig: any = {
      parentComponent: this,
      parentName: 'student-tasks-notes-panel',
      primaryEmptyStateHeader: 'No notes added',
      linkUrl: null,
      linkLabel: null,
    };
    if (this.modalControl === 'STUDENT') {
      noRowOverlayConfig.showButton = true;
      noRowOverlayConfig.buttonText = 'Add note';
      noRowOverlayConfig.secondaryEmptyStateMessage = 'Add notes to share information with your colleagues or log your interactions with students and their families.';
      noRowOverlayConfig.disabledButton = false;
    }
    return noRowOverlayConfig;
  }

  private _processPaginatedData (data: IEcfikNotesRes): void {
    const documents = data.data;
    const sortedData = this._sortByDateField(documents, 'dateCreated');

    this.rowData = sortedData;
    this.gridApi?.setGridOption('rowData', this.rowData); // Needs to call in order for custom render to pick up changes
    this.currentPage = data.pages.current;
    this.totalRows = data.items.total;
    this.totalPages = Math.ceil(this.totalRows / this.pageSize);
    this.gridApi?.redrawRows();
  }

  private _sortByDateField (data: any[], sortField): any[] {
    const sortedData = data.sort((document1: any, document2: any) => {
      const date1Timestamp = new Date(document1[sortField]).getTime();
      const date2Timestamp = new Date(document2[sortField]).getTime();
      return date2Timestamp - date1Timestamp;
    });

    return sortedData;
  }

  public onActionSuccess ({ toastText }: { toastText?: string | null } = {}) {
    this.spinner = this.spinnerService.openSpinner({ message: 'Loading...' });
    if (toastText) {
      this.snackBarService.showToast({ toastText });
    }
    this.saveStudent.emit();
    this.currentPage = 1;
    this.refreshDataForStudent(this.currentPage);
  }

  // called in `history-all-modal-more-button.component.ts` and 'no-rows.component.ts`
  public onAddNote (): void {
    if (this.modalControl === 'STUDENT') {
      const studentData = this.data.studentInfo;
      const data: ICheckInLogData = {
        modalControl: 'ADD_CHECKIN_LOG',
        schoolId: this.schoolId,
        studentId: studentData.id,
        studentName: `${studentData.lastName}, ${studentData.firstName}`,
      };

      this.modalsService
        .openCheckInLogModal(data)
        .afterClosed()
        .pipe(
          switchMap(form => {
            if (form) {
              const payload = Object.entries(form).reduce(
                (acc, [key, value]) => {
                  acc[key] = value === '' ? null : value;
                  return acc;
                }, {},
              ) as ISchoolNote;
              return this.apiService.createNote(payload);
            } else {
              return of(null);
            }
          }),
        )
        .subscribe(res => {
          if (res) {
            this.onActionSuccess({ toastText: 'Note has been added' });
            this.shouldRefreshParent = true;
          }
        });
    }
  }

  // called in `history-all-modal-more-button.component.ts`
  public editNoteButton (listType, docId, noteData) {
    const mode = 'EDIT_PROFILE';
    const data: INotesData = {
      docId,
      partnerType: PartnerTypes.SCHOOL,
      school: this.currentSchool,
      currentUser: this.currentUser,
      note: this.currentSelectedRow,
      mode,
      studentName: noteData.attachments.student.lastFirst,
      studentId: noteData.attachments.student.studentId,
      listType,
      isStudentECFIKEligible: true,
    };
    this.modalsService
      .openNoteModal(data)
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.onActionSuccess({ toastText: 'Note has been updated' });
          this.shouldRefreshParent = true;
        }
      });
  }

  public deleteNoteButton (docId: any) {
    const data: IConfirmModalComponentData = {
      title: 'Delete note',
      message: 'Are you sure you would like to delete this note? This note will be removed from the student\'s records.',
      confirmText: 'Delete',
    };

    this.modalsService
      .openConfirmModal(data)
      .afterClosed()
      .pipe(
        switchMap((confirmed: boolean) => {
          if (confirmed) {
            const noteId = docId;
            return this.apiService.patchNote(noteId, { status: 'DELETED' }, this.partnerType);
          } else {
            return of(false);
          }
        },
        ),
      ).subscribe(res => {
        if (res) {
          this.onActionSuccess({ toastText: 'Note has been deleted' });
          this.shouldRefreshParent = true;
        }
      });
  }

  public onBtNext (action: number) {
    this.refreshDataForStudent(action);
  }

  public onBtPrevious (action: number) {
    this.refreshDataForStudent(action);
  }

  public close () {
    this.dialogRef.close(this.shouldRefreshParent);
  }
}
